import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class CoreFilterRangePlugin implements Plugin {
  private root: HTMLElement;
  private container: HTMLElement;
  private minRange: HTMLInputElement;
  private maxRange: HTMLInputElement;
  private fill: HTMLElement;
  private minVal: HTMLElement;
  private maxVal: HTMLElement;
  private tooltip: HTMLElement;
  private minCoreInputField: HTMLInputElement | null;
  private maxCoreInputField: HTMLInputElement | null;
  private minLimit: number;
  private maxLimit: number;
  private step: number;
  private suffix: string;
  private isDragging: boolean = false;
  private activeThumb: "min" | "max" | null = null;
  private startMinValue: number = 0;
  private startMaxValue: number = 0;

  constructor(element: HTMLElement) {
    this.root = element;
    this.container = element.querySelector(
      ".core-filter-range__container",
    ) as HTMLElement;
    this.minRange = element.querySelector(
      ".core-filter-range__input--min",
    ) as HTMLInputElement;
    this.maxRange = element.querySelector(
      ".core-filter-range__input--max",
    ) as HTMLInputElement;
    this.fill = element.querySelector(
      ".core-filter-range__fill",
    ) as HTMLElement;
    this.minVal = element.querySelector('[id^="minVal-"]') as HTMLElement;
    this.maxVal = element.querySelector('[id^="maxVal-"]') as HTMLElement;
    this.tooltip = element.querySelector(
      ".core-filter-range__tooltip",
    ) as HTMLElement;

    const minCoreInputId = element
      .querySelector('[id^="core-input-min-"]')
      ?.getAttribute("id");
    const maxCoreInputId = element
      .querySelector('[id^="core-input-max-"]')
      ?.getAttribute("id");

    this.minCoreInputField = minCoreInputId
      ? (element.querySelector(`#${minCoreInputId}`) as HTMLInputElement)
      : null;
    this.maxCoreInputField = maxCoreInputId
      ? (element.querySelector(`#${maxCoreInputId}`) as HTMLInputElement)
      : null;

    this.minLimit = parseInt(element.dataset.min || "0", 10);
    this.maxLimit = parseInt(element.dataset.max || "100", 10);
    this.step = parseFloat(element.dataset.step || "1");
    this.suffix = element.dataset.valueSuffix || "";
  }

  init(): void {
    if (!this.container || !this.minRange || !this.maxRange || !this.fill) {
      console.error("CoreFilterRange: elementos requeridos no encontrados");
      return;
    }

    if (!this.tooltip) {
      console.warn("CoreFilterRange: tooltip no encontrado");
    }

    if (parseInt(this.minRange.value, 10) > parseInt(this.maxRange.value, 10)) {
      this.minRange.value = this.maxRange.value;
    }

    if (this.tooltip) {
      this.tooltip.style.position = "absolute";
      this.tooltip.style.top = "-40px";
      this.tooltip.style.left = "0";
      this.tooltip.style.transform = "translateX(-50%)";
      this.tooltip.style.opacity = "0";
      this.tooltip.style.visibility = "hidden";
      this.tooltip.style.display = "block";
      this.tooltip.style.zIndex = "10";
      this.tooltip.style.pointerEvents = "none";
    }

    const initialMinValue = parseInt(this.minRange.value, 10);
    const initialMaxValue = parseInt(this.maxRange.value, 10);

    this.startMinValue = initialMinValue;
    this.startMaxValue = initialMaxValue;

    if (this.minCoreInputField) {
      this.minCoreInputField.value = String(initialMinValue) + this.suffix;
    }
    if (this.maxCoreInputField) {
      this.maxCoreInputField.value = String(initialMaxValue) + this.suffix;
    }

    const currentMinValue = initialMinValue;
    const currentMaxValue = initialMaxValue;
    const currentMinPercent =
      ((currentMinValue - this.minLimit) / (this.maxLimit - this.minLimit)) *
      100;
    const currentMaxPercent =
      ((currentMaxValue - this.minLimit) / (this.maxLimit - this.minLimit)) *
      100;
    const activeLeft = Math.max(
      0,
      Math.min(100, Math.min(currentMinPercent, currentMaxPercent)),
    );
    const activeRight = Math.max(
      0,
      Math.min(100, Math.max(currentMinPercent, currentMaxPercent)),
    );
    const fillWidth = Math.max(0, activeRight - activeLeft);

    this.fill.style.cssText = `
      position: absolute;
      top: 50%;
      left: ${activeLeft}%;
      width: ${fillWidth}%;
      height: 4px;
      background: var(--filter-slider-active-bg-def, #393);
      transform: translateY(-50%);
      transition: none;
      z-index: 1;
      display: block;
      pointer-events: none;
      border-radius: 208px 208px 208px 208px;
      opacity: 1;
      visibility: visible;
    `
      .replace(/\s+/g, " ")
      .trim();

    const initializeRange = () => {
      if (this.container.offsetWidth > 0) {
        this.updateRange();
      } else {
        requestAnimationFrame(initializeRange);
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(initializeRange);
    });

    this.minRange.addEventListener("input", () => {
      if (
        parseInt(this.minRange.value, 10) > parseInt(this.maxRange.value, 10)
      ) {
        this.minRange.value = this.maxRange.value;
      }
      if (!this.isDragging) {
        this.isDragging = true;
        this.activeThumb = "min";
      }
      this.updateRange();
    });

    this.maxRange.addEventListener("input", () => {
      if (
        parseInt(this.maxRange.value, 10) < parseInt(this.minRange.value, 10)
      ) {
        this.maxRange.value = this.minRange.value;
      }
      if (!this.isDragging) {
        this.isDragging = true;
        this.activeThumb = "max";
      }
      this.updateRange();
    });
    this.minRange.addEventListener("mousedown", (e) => {
      this.isDragging = true;
      this.activeThumb = "min";
      this.minRange.style.zIndex = "10";
      this.maxRange.style.zIndex = "5";
      this.startMinValue = parseInt(this.minRange.value, 10);
      this.startMaxValue = parseInt(this.maxRange.value, 10);
      this.updateRange();
    });

    this.maxRange.addEventListener("mousedown", (e) => {
      this.isDragging = true;
      this.activeThumb = "max";
      this.maxRange.style.zIndex = "10";
      this.minRange.style.zIndex = "5";
      this.startMinValue = parseInt(this.minRange.value, 10);
      this.startMaxValue = parseInt(this.maxRange.value, 10);
      this.updateRange();
    });

    const handleMouseMove = (e: MouseEvent) => {
      if (this.isDragging && this.activeThumb) {
        requestAnimationFrame(() => {
          this.updateRange();
        });
      }
    };

    document.addEventListener("mousemove", handleMouseMove);

    const track = this.container.querySelector(
      ".core-filter-range__track",
    ) as HTMLElement;

    const handleTrackMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Capture start values for track clicks too
      this.startMinValue = parseInt(this.minRange.value, 10);
      this.startMaxValue = parseInt(this.maxRange.value, 10);

      if (target === this.minRange || target === this.maxRange) {
        const rect = track.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickPercent = (clickX / rect.width) * 100;

        const minValue = parseInt(this.minRange.value, 10);
        const maxValue = parseInt(this.maxRange.value, 10);
        const minPercent =
          ((minValue - this.minLimit) / (this.maxLimit - this.minLimit)) * 100;
        const maxPercent =
          ((maxValue - this.minLimit) / (this.maxLimit - this.minLimit)) * 100;

        const distanceToMin = Math.abs(clickPercent - minPercent);
        const distanceToMax = Math.abs(clickPercent - maxPercent);

        if (target === this.minRange && distanceToMax < distanceToMin) {
          e.preventDefault();
          e.stopPropagation();
          this.isDragging = true;
          this.activeThumb = "max";
          this.maxRange.style.zIndex = "10";
          this.minRange.style.zIndex = "5";
          this.maxRange.focus();
          const newEvent = new MouseEvent("mousedown", {
            bubbles: true,
            cancelable: true,
            clientX: e.clientX,
            clientY: e.clientY,
            button: e.button,
            buttons: e.buttons,
          });
          this.maxRange.dispatchEvent(newEvent);
          this.updateRange();
          return;
        } else if (target === this.maxRange && distanceToMin < distanceToMax) {
          e.preventDefault();
          e.stopPropagation();
          this.isDragging = true;
          this.activeThumb = "min";
          this.minRange.style.zIndex = "10";
          this.maxRange.style.zIndex = "5";
          this.minRange.focus();
          const newEvent = new MouseEvent("mousedown", {
            bubbles: true,
            cancelable: true,
            clientX: e.clientX,
            clientY: e.clientY,
            button: e.button,
            buttons: e.buttons,
          });
          this.minRange.dispatchEvent(newEvent);
          this.updateRange();
          return;
        }

        if (target === this.minRange) {
          this.isDragging = true;
          this.activeThumb = "min";
          this.minRange.style.zIndex = "10";
          this.maxRange.style.zIndex = "5";
          this.updateRange();
        } else if (target === this.maxRange) {
          this.isDragging = true;
          this.activeThumb = "max";
          this.maxRange.style.zIndex = "10";
          this.minRange.style.zIndex = "5";
          this.updateRange();
        }
      }
    };

    if (track) {
      track.addEventListener("mousedown", handleTrackMouseDown, true);
    }

    const restoreZIndex = () => {
      this.isDragging = false;
      this.activeThumb = null;

      if (this.tooltip) {
        this.tooltip.style.opacity = "0";
        this.tooltip.style.visibility = "hidden";
      }

      this.minRange.style.zIndex = "6";
      this.maxRange.style.zIndex = "5";
      this.updateRange();

      const currentMinValue = parseInt(this.minRange.value, 10);
      const currentMaxValue = parseInt(this.maxRange.value, 10);

      if (
        currentMinValue !== this.startMinValue ||
        currentMaxValue !== this.startMaxValue
      ) {
        this.root.dispatchEvent(
          new CustomEvent("core-filter-range:change", {
            bubbles: true,
            detail: {
              min: currentMinValue,
              max: currentMaxValue,
              oldMin: this.startMinValue,
              oldMax: this.startMaxValue,
            },
          }),
        );
        // Update start values to prevent duplicate emissions if both mouseup and change fire
        this.startMinValue = currentMinValue;
        this.startMaxValue = currentMaxValue;
      }
    };

    this.minRange.addEventListener("mouseup", restoreZIndex);
    this.maxRange.addEventListener("mouseup", restoreZIndex);
    this.minRange.addEventListener("change", restoreZIndex);
    this.maxRange.addEventListener("change", restoreZIndex);
    document.addEventListener("mouseup", restoreZIndex);

    if (this.minCoreInputField) {
      this.minCoreInputField.addEventListener("input", (e) => {
        this.handleMinInputChange(e);
      });
      this.minCoreInputField.addEventListener("change", (e) => {
        this.handleMinInputChange(e);
      });
    }

    if (this.maxCoreInputField) {
      this.maxCoreInputField.addEventListener("input", (e) => {
        this.handleMaxInputChange(e);
      });
      this.maxCoreInputField.addEventListener("change", (e) => {
        this.handleMaxInputChange(e);
        this.handleMaxInputChange(e);
      });
    }

    this.root.addEventListener("core-filter-range:reset", ((e: CustomEvent) => {
      const silent = e.detail?.silent ?? false;
      this.reset({ silent });
    }) as EventListener);

    this.root.addEventListener("core-filter-range:requestState", () => {
      this.root.dispatchEvent(
        new CustomEvent("core-filter-range:state", {
          bubbles: true,
          detail: {
            min: this.minRange.value,
            max: this.maxRange.value,
          },
        }),
      );
    });

    this.root.addEventListener("core-filter-range:setState", ((
      e: CustomEvent,
    ) => {
      const {
        min,
        max,
        silent = false,
      } = e.detail as {
        min: number | string;
        max: number | string;
        silent?: boolean;
      };

      if (min !== undefined) {
        const minVal = Math.max(
          this.minLimit,
          Math.min(this.maxLimit, Number(min)),
        );
        this.minRange.value = String(minVal);
        if (this.minCoreInputField) {
          this.minCoreInputField.value = minVal + this.suffix;
        }
      }
      if (max !== undefined) {
        const maxVal = Math.max(
          this.minLimit,
          Math.min(this.maxLimit, Number(max)),
        );
        this.maxRange.value = String(maxVal);
        if (this.maxCoreInputField) {
          this.maxCoreInputField.value = maxVal + this.suffix;
        }
      }

      this.startMinValue = parseInt(this.minRange.value, 10);
      this.startMaxValue = parseInt(this.maxRange.value, 10);
      this.updateRange({ silent });
    }) as EventListener);
  }

  private reset(options?: { silent?: boolean }): void {
    const silent = options?.silent ?? false;
    this.minRange.value = String(this.minLimit);
    this.maxRange.value = String(this.maxLimit);

    if (this.minCoreInputField) {
      this.minCoreInputField.value = this.minLimit + this.suffix;
    }
    if (this.maxCoreInputField) {
      this.maxCoreInputField.value = this.maxLimit + this.suffix;
    }

    this.startMinValue = this.minLimit;
    this.startMaxValue = this.maxLimit;

    this.updateRange({ silent });
  }

  private updateRange(options?: { silent?: boolean }): void {
    const silent = options?.silent ?? false;
    const minValue = parseInt(this.minRange.value, 10);
    const maxValue = parseInt(this.maxRange.value, 10);

    const minPercent =
      ((minValue - this.minLimit) / (this.maxLimit - this.minLimit)) * 100;
    const maxPercent =
      ((maxValue - this.minLimit) / (this.maxLimit - this.minLimit)) * 100;

    const activeLeft = Math.max(
      0,
      Math.min(100, Math.min(minPercent, maxPercent)),
    );
    const activeRight = Math.max(
      0,
      Math.min(100, Math.max(minPercent, maxPercent)),
    );
    const fillWidth = Math.max(0, activeRight - activeLeft);

    // Actualizar fill
    this.fill.style.left = activeLeft + "%";
    this.fill.style.width = fillWidth + "%";
    this.fill.style.zIndex = "1";

    if (this.isDragging) {
      if (this.activeThumb === "min" && this.minCoreInputField) {
        this.minCoreInputField.value = minValue + this.suffix;
      }
      if (this.activeThumb === "max" && this.maxCoreInputField) {
        this.maxCoreInputField.value = maxValue + this.suffix;
      }
    }

    const track = this.container.querySelector(
      ".core-filter-range__track",
    ) as HTMLElement;
    const trackWidth = track ? track.offsetWidth : this.container.offsetWidth;

    if (this.tooltip) {
      this.tooltip.style.position = "absolute";

      if (this.isDragging && this.activeThumb) {
        let tooltipValue: string;
        let thumbCenterPosition: number;

        if (this.activeThumb === "min") {
          if (this.minCoreInputField && this.minCoreInputField.value) {
            tooltipValue = this.minCoreInputField.value;
          } else {
            tooltipValue = minValue + this.suffix;
          }
          thumbCenterPosition = (minPercent / 100) * trackWidth;
        } else {
          if (this.maxCoreInputField && this.maxCoreInputField.value) {
            tooltipValue = this.maxCoreInputField.value;
          } else {
            tooltipValue = maxValue + this.suffix;
          }
          thumbCenterPosition = (maxPercent / 100) * trackWidth;
        }

        this.tooltip.textContent = tooltipValue;
        this.tooltip.style.left = thumbCenterPosition + "px";
        this.tooltip.style.top = "-40px";
        this.tooltip.style.transform = "translateX(-50%)";
        this.tooltip.style.opacity = "1";
        this.tooltip.style.visibility = "visible";
        this.tooltip.style.zIndex = "20";
        this.tooltip.style.display = "block";
      } else {
        this.tooltip.style.opacity = "0";
        this.tooltip.style.visibility = "hidden";
      }
    }
  }

  private handleMinInputChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    const value = input.value.replace(/[^\d.-]/g, "");
    const numValue = parseFloat(value) || this.minLimit;

    const clampedValue = Math.max(
      this.minLimit,
      Math.min(this.maxLimit, numValue),
    );

    const maxValue = parseInt(this.maxRange.value, 10);
    const finalValue = Math.min(clampedValue, maxValue);

    this.minRange.value = String(finalValue);

    input.value = finalValue + this.suffix;

    this.updateRange();
  }

  private handleMaxInputChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    const value = input.value.replace(/[^\d.-]/g, "");
    const numValue = parseFloat(value) || this.maxLimit;

    const clampedValue = Math.max(
      this.minLimit,
      Math.min(this.maxLimit, numValue),
    );

    const minValue = parseInt(this.minRange.value, 10);
    const finalValue = Math.max(clampedValue, minValue);

    this.maxRange.value = String(finalValue);

    input.value = finalValue + this.suffix;

    this.updateRange();
  }
}

PluginRegistry.register("core-filter-range", CoreFilterRangePlugin);
